/**
 * Client-side password-complexity policy for the Console auth surface.
 *
 * MIRRORS the backend policy in `backend/app/core/password_policy.py` (plus the
 * 8-128 length bound each auth model carries on its Pydantic `Field`) so that
 * `/auth/register`, `/console/auth/register`, and `/auth/password` never round
 * trip a password the user could have been told about up front. The backend is
 * still the source of truth: its 422 detail is rendered readably (via
 * `api.readDetail`) if a request ever slips through.
 *
 * Pure module: no React, no network, no DOM. This is a DETERMINISTIC rule set,
 * not a fuzzy strength score, so there is intentionally NO zxcvbn / extra
 * dependency. The character classes are ASCII to match the backend regex
 * exactly (symbols and unicode are allowed but never SATISFY a class).
 */

// Length bounds (mirror PASSWORD_MIN_LENGTH / PASSWORD_MAX_LENGTH on the backend).
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

// Human-readable statement of the whole policy, sourced by both the register
// and change-password hints so the two surfaces stay consistent. Mirrors the
// backend PASSWORD_POLICY_MESSAGE copy; no em dashes (house style).
export const PASSWORD_POLICY_HINT =
  "8 to 128 characters, including a lowercase letter, an uppercase letter, and a digit.";

/**
 * Evaluate a candidate password against the policy.
 *
 * @param {string} pw
 * @returns {{minLength:boolean, maxLength:boolean, hasLower:boolean,
 *            hasUpper:boolean, hasDigit:boolean, valid:boolean}}
 *   Per-requirement booleans plus the overall `valid` flag. `valid` is true iff
 *   the length is in [8, 128] AND all three character classes are present.
 */
export function checkPassword(pw) {
  const value = typeof pw === "string" ? pw : "";
  // Count Unicode CODE POINTS, not UTF-16 code units, so the 8/128 length bounds
  // agree with the backend byte-for-byte. Python's `len(str)` (and Pydantic's
  // Field min_length/max_length) counts code points, so an astral char (e.g. an
  // emoji) is 1 there but 2 via JS `value.length`; the spread iterates code
  // points and keeps both directions of the boundary in parity.
  const len = [...value].length;
  const minLength = len >= PASSWORD_MIN_LENGTH;
  const maxLength = len <= PASSWORD_MAX_LENGTH;
  const hasLower = /[a-z]/.test(value);
  const hasUpper = /[A-Z]/.test(value);
  const hasDigit = /[0-9]/.test(value);
  const valid = minLength && maxLength && hasLower && hasUpper && hasDigit;
  return { minLength, maxLength, hasLower, hasUpper, hasDigit, valid };
}

/**
 * Requirement list — the SINGLE SOURCE OF TRUTH consumed by the checklist UI
 * (`PasswordRequirements`) and the unit tests alike. Each entry is a stable
 * `key`, a user-facing `label` (no em dashes), and a `met(result)` predicate
 * that reads a `checkPassword(...)` result. The length item is a single line
 * that is met only when BOTH the min and max bounds hold.
 */
export const PASSWORD_REQUIREMENTS = [
  { key: "length", label: "8 to 128 characters", met: (r) => r.minLength && r.maxLength },
  { key: "hasLower", label: "A lowercase letter", met: (r) => r.hasLower },
  { key: "hasUpper", label: "An uppercase letter", met: (r) => r.hasUpper },
  { key: "hasDigit", label: "A digit", met: (r) => r.hasDigit },
];
